<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import { useAuth } from '@/composables/useAuth'
import MembersTable from '@/components/organizer/team/MembersTable.vue'
import InvitationsPanel from '@/components/organizer/team/InvitationsPanel.vue'
import TabStrip from '@/components/shared/TabStrip.vue'

type TabKey = 'members' | 'invitations'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const tabs = computed(() => [
  { key: 'members' as TabKey, label: t('team.members.heading') },
  { key: 'invitations' as TabKey, label: t('team.invitations.heading') },
])

const activeTab = computed((): TabKey => {
  const tab = route.query['tab'] as string | undefined
  return tab === 'invitations' ? 'invitations' : 'members'
})

function setTab(tab: TabKey): void {
  void router.push({ query: { ...route.query, tab } })
}

const organisation = computed(() => user.value?.organisations[0] ?? null)
const organisationId = computed<number | null>(() => organisation.value?.id ?? null)
const currentUserId = computed<number | null>(() => user.value?.id ?? null)
const isOwner = computed<boolean>(() => organisation.value?.role === 'owner')
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white">{{ t('team.page.title') }}</h1>
        <p class="text-slate-400 mt-1">{{ t('team.page.subtitle') }}</p>
      </div>

      <!-- No organisation guard -->
      <div
        v-if="!organisationId || !currentUserId"
        class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 text-sm text-slate-400"
      >
        {{ t('common.error_generic') }}
      </div>

      <template v-else>
        <!-- Tab strip -->
        <TabStrip
          :model-value="activeTab"
          :tabs="tabs"
          class="mb-6"
          @update:model-value="setTab($event as TabKey)"
        />

        <!-- Members tab -->
        <MembersTable
          v-if="activeTab === 'members'"
          :organisation-id="organisationId"
          :current-user-id="currentUserId"
          :is-owner="isOwner"
        />

        <!-- Invitations tab -->
        <InvitationsPanel
          v-else-if="activeTab === 'invitations'"
          :organisation-id="organisationId"
          :is-owner="isOwner"
        />
      </template>
    </main>
  </OrganizerLayout>
</template>
